import { GUARD_ARMED_STATUS, JOB_STATUSES, JOB_TYPES, REPEAT_CUSTOM_TYPE, REPEAT_CYCLE, SHIFT_STATUSES } from './enums';
import { ReviewInterface } from './review';

export type JobTypes = JOB_TYPES.ONE_TIME | JOB_TYPES.ONGOING;

export type JobStatusType =
    | JOB_STATUSES.ACCEPTED
    | JOB_STATUSES.CANCELLED
    | JOB_STATUSES.COMPLETED
    | JOB_STATUSES.CONFIRMED
    | JOB_STATUSES.PENDING
    | JOB_STATUSES.REJECTED;

export type ShiftStatusType =
    | SHIFT_STATUSES.ACCEPTED
    | SHIFT_STATUSES.NOT_APPLIED
    | SHIFT_STATUSES.PENDING
    | SHIFT_STATUSES.REJECTED
    | SHIFT_STATUSES.CALLED_OFF
    | SHIFT_STATUSES.CANCELLED;

export type EventTypeObject = {
    id: number;
    label: string;
    value: string;
};

export type GuardArmedStatusTypes = GUARD_ARMED_STATUS.ARMED | GUARD_ARMED_STATUS.UNARMED;

export type GuardListSortOptions =
    | 'Most relevant'
    | 'Distance from near to far'
    | 'Rate from high to low'
    | 'Rate from low to high';

export type RepeatCycleTypes =
    | REPEAT_CYCLE.WEEKLY
    | REPEAT_CYCLE.MONTHLY
    | REPEAT_CYCLE.FORTNIGHTLY
    | REPEAT_CYCLE.CUSTOM;

export type RepeatCustomTypes = REPEAT_CUSTOM_TYPE.MONTHLY | REPEAT_CUSTOM_TYPE.WEEKLY;

export type GuardJobRepeatCycle = {
    repeat_status: string;
    repeat_cycle: string;
    repeat_frequency: number;
    repeat_type: string;
    repeat_days: string[];
    repeat_end_date: string;
    repeat_explained: string;
};

export type GuardJobItem = {
    job_id: number;
    job_type: JOB_TYPES.ONE_TIME | JOB_TYPES.ONGOING | string;
    event_type: string;
    is_urgent: boolean;
    created_at: string;
    updated_at: string;
    client: {
        id: number;
        photo_url: string | null;
        rating: number;
    };
    distance_from_job: number;
    rate: string;
    repeat_cycle: GuardJobRepeatCycle | null;
    shifts: GuardJobShiftItem[];
    status?: JobStatusType;
};

export type ShiftNote = {
    id: number;
    job_shift_guard_id?: number;
    from: string;
    note_type: string;
    note: string;
    created_at: string;
    updated_at: string | null;
};

export const isGuardAllowedToApplyToShiftItem = (shift: GuardJobShiftItem): boolean => {
    return shift.status === SHIFT_STATUSES.NOT_APPLIED || shift.status === SHIFT_STATUSES.CANCELLED;
};

export type GuardJobShiftItem = {
    id: number;
    status: ShiftStatusType;
    start_date: string;
    start_time: string;
    end_date: string;
    end_time: string;
    start_display: string;
    end_display: string;
    start_relative: string;
    is_morning: boolean;
    is_evening: boolean;
    is_overnight: boolean;
    matches_schedule?: boolean;
    rate?: string;
    event_type?: string;
    client_name?: string;
    client_profile_image?: string | null;
    job_id?: number;
    job_shift_id?: number;
    is_repeatable?: boolean;
};

export type GuardOwnShiftItem = {
    id: number;
    status: ShiftStatusType;
    start_date: string;
    start_time: string;
    end_date: string;
    end_time: string;
    event_type: string | null;
    distance_from_job: number;
    rate: string;
    client_profile_image: string | null;
};

export type GuardJobDetails = {
    id: number;
    job_type: JOB_TYPES.ONE_TIME | JOB_TYPES.ONGOING | string;
    repeat_job_id: number | null;
    repeat_cycle: GuardJobRepeatCycle | null;
    event_type: string;
    status: JobStatusType;
    is_urgent: boolean;
    created_at: string;
    updated_at: string;
    client: {
        id: number;
        photo_url: string | null;
        name: string;
    };
    location: {
        address: string;
        city: string;
        state: string | null;
        zip: string | null;
        latitude: number;
        longitude: number;
    };
    distance_from_job: number;
    shifts: GuardJobShiftItem[];
    rate: string;
    event_details: string | null;
    primary_contact_name: string;
    primary_contact_phone: string;
    primary_contact_email: string;
    job_description: string;
    previous_security_issues: string | null;
    emergency_procedures: string | null;
    primary_guard_duties: string | null;
    uniform_type: string | null;
    uniform_provider: string | null;
    guard_armed_status: string | null;
};

export type GuardShiftDetails = {
    id: number;
    status: ShiftStatusType;
    start_date: string;
    start_time: string;
    end_date: string;
    end_time: string;
    rate: string;
    is_repeatable: boolean;
    client: {
        id: number;
        photo_url: string | null;
        name: string;
    };
    job: {
        id: number;
        status: JobStatusType;
        event_type: string | null;
        location: {
            address: string;
            city: string;
            state: string;
            zip: string;
        };
        uniform_type: string;
        uniform_provider: string;
        repeat_cycle: GuardJobRepeatCycle | null;
        review: {
            stars: number;
            review: string;
        } | null;
    };
    notes: ShiftNote[];
};

export type JobClientDetails = {
    name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    photo_url: string | null;
    star_rating: number | null;
    posted_jobs_count: number;
    reviews: ReviewInterface[];
};
